// API/foodOrders.js

import {fetchGet, fetchPost, fetchPut} from "./util";
import {format, isValid} from "date-fns";

// Helper function to format time-only fields (e.g., "08:30")
const formatTime = (value) => {
    if (!value) return "";
    try {
        const date = new Date(`2025-08-12T${value}:00`);
        return isValid(date) ? format(date, "HH:mm") : "";
    } catch {
        return "";
    }
};

// Helper function to format date/time safely
const formatDateTime = (value, formatString = "yyyy-MM-dd HH:mm") => {
    if (!value || !isValid(new Date(value))) return "";
    return format(new Date(value), formatString);
};

// Helper function to expand Excel-like functions
const expandExcelFunctions = (input) => {
    let expression = input;

    // Expand SUM function: SUM(a, b, c) → (a + b + c)
    expression = expression.replace(/SUM\s*\(([^)]+)\)/gi, (_match, args) => {
        const argList = args.split(',').map(arg => arg.trim());
        return '(' + argList.join(' + ') + ')';
    });

    // Expand MAX function: MAX(a, b, c) → Math.max(a, b, c)
    expression = expression.replace(/MAX\s*\(([^)]+)\)/gi, (_match, args) => {
        const argList = args.split(',').map(arg => arg.trim());
        return 'Math.max(' + argList.join(', ') + ')';
    });

    // Expand MIN function: MIN(a, b, c) → Math.min(a, b, c)
    expression = expression.replace(/MIN\s*\(([^)]+)\)/gi, (_match, args) => {
        const argList = args.split(',').map(arg => arg.trim());
        return 'Math.min(' + argList.join(', ') + ')';
    });

    // Expand ROUND function: ROUND(value, decimals) → Math.round(value)
    expression = expression.replace(/ROUND\s*\(([^)]+)\)/gi, (_match, args) => {
        const argList = args.split(',').map(arg => arg.trim());
        const value = argList[0];
        const decimals = argList[1] ? parseInt(argList[1]) : 0;
        return decimals === 0 ? `Math.round(${value})` : `Math.round(${value} * Math.pow(10, ${decimals})) / Math.pow(10, ${decimals})`;
    });

    // Expand CEIL function: CEIL(value) → Math.ceil(value)
    expression = expression.replace(/CEIL\s*\(([^)]+)\)/gi, 'Math.ceil($1)');

    // Expand FLOOR function: FLOOR(value) → Math.floor(value)
    expression = expression.replace(/FLOOR\s*\(([^)]+)\)/gi, 'Math.floor($1)');

    // Expand ABS function: ABS(value) → Math.abs(value)
    expression = expression.replace(/ABS\s*\(([^)]+)\)/gi, 'Math.abs($1)');

    // Expand IF function: IF(condition, true_value, false_value) → (condition ? true_value : false_value)
    expression = expression.replace(/IF\s*\(([^)]+)\)/gi, (match, args) => {
        const parts = [];
        let current = '';
        let depth = 0;
        let inQuotes = false;

        for (let i = 0; i < args.length; i++) {
            const char = args[i];
            if (char === '"' || char === "'") {
                inQuotes = !inQuotes;
            } else if (!inQuotes) {
                if (char === '(') depth++;
                else if (char === ')') depth--;
                else if (char === ',' && depth === 0) {
                    parts.push(current.trim());
                    current = '';
                    continue;
                }
            }
            current += char;
        }
        parts.push(current.trim());

        if (parts.length === 3) {
            return `(${parts[0]} ? ${parts[1]} : ${parts[2]})`;
        }
        return match; // Return original if not 3 parts
    });

    return expression;
};


export const getFoodOrders = async (pageSize, pageIndex = 0, query = {}, sort = {}) => {
    const queryParams = new URLSearchParams();

    // Add query parameters as simple key-value pairs
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            queryParams.append(key, value);
        }
    });

    // Build final URL
    const url = `/food-orders?results=${pageSize}&offset=${pageIndex * pageSize}&${queryParams.toString()}${sort}`

    try {

        return await fetchGet(url);

    } catch (err) {
        throw {error: err.message || "Failed to fetch food orders"};
    }
};

export const updateFoodOrder = async (id, values) => {
    // Helper function to format time fields as HH:mm
    const formatTime = (value) => {
        if (!value || !isValid(new Date(value))) return "";
        return format(new Date(value), "HH:mm");
    };
    const formatDateTime = (value, formatString = "yyyy-MM-dd HH:mm") => {
        if (!value || !isValid(new Date(value))) return "";
        return format(new Date(value), formatString);
    };

    const updatedOrder = {
        caterer_id: values.caterer_id ?? null,
        fika: values.fika ?? false,
        comments: values.comment || "",
        food_considrations: values.food_considrations || "",
        delivery_time: values.delivery_time ? formatTime(values.delivery_time) : "",
        end_time: values.end_time ? formatTime(values.end_time) : "",
        fika_time: values.fika_time ? formatTime(values.fika_time) : "",
        is_pre_order_email_content_ready: values.is_pre_order_email_content_ready ?? false,
        is_final_order_email_content_ready: values.is_final_order_email_content_ready ?? false,
        pre_order_email_trigger_date: values.pre_order_email_trigger_date ? formatDateTime(values.pre_order_email_trigger_date) : null,
        final_order_email_trigger_date: values.final_order_email_trigger_date ? formatDateTime(values.final_order_email_trigger_date) : null,
        pre_order_email_subject: values.pre_order_email_subject || "",
        pre_order_email_body: values.pre_order_email_body || "",
        final_order_email_subject: values.final_order_email_subject || "",
        final_order_email_body: values.final_order_email_body || "",
        order_config: {
            rules: {
                delivery: values.delivery || "",
                formula: values.order_formula || "",
            },
            email: {
                template_pre_id: values.pre_email_template_id || null,
                template_final_id: values.final_email_template_id || null,
            },
        },
        formula_override: values.order_formula || "",
    };
    if (values.location.toLowerCase() === "se") {
        updatedOrder.end_time = values.end_time ? formatTime(values.end_time) : ""
    }

    return fetchPut(`/food-orders/${id}`, updatedOrder).then((res) => res);
};

export const getEmailTemplates = async () => {
    return fetchGet(`/food-orders/email-templates`).then(res => res);
};

export const previewEmail = async (values) => {
    let id = values.food_order_id;
    const payload = {
        food_order_id: values.food_order_id,
        email_subject: values.email_subject,
        email_body: values.email_body
    }
    return fetchPost(`/food-orders/${id}/email-template-preview`, payload).then(res => res);
};

export const sendTestEmail = async ({
                                        id,
                                        email,
                                        email_subject,
                                        email_body
                                    }) => {
    const payload = {
        email,
        email_subject,
        email_body: email_body
    }
    return fetchPost(`/food-orders/${id}/test-email`, payload).then(res => res);
};
